/* Crybaby Kit Builder
 * Vanilla JS, no jQuery, no Alpine. Native replacement for Fast Bundle's widget.
 * Cart model (set pricing, per Cat 2026-06-10): adds ONE line, the kit parent
 * product at its own price ($75/$45); the picked options are recorded as visible
 * line item properties for fulfillment and are never charged separately.
 * Checked atc-upsells are merged into the same /cart/add.js call (still charged
 * at their own prices) because this handler suppresses the theme.liquid upsell
 * handler on kit PDPs. The document click listener registers at script execution
 * (parser-blocking load) so it wins capture order over the theme.liquid listener.
 *
 * 2026-09-01 responsive pass: categories with exactly one available option are
 * selected automatically and collapsed ("Included"), the first unresolved
 * required row opens on load, and Enter-key form submits are guarded the same
 * way as button clicks. The Add to Cart button is never disabled: an early tap
 * scrolls to and flashes the missing row instead of dying silently.
 */
(function () {
  'use strict';

  if (window.__kitBuilderHandler) return;
  window.__kitBuilderHandler = true;

  var MONEY_FORMAT = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, MONEY_FORMAT);
      }
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  function findWidget(form) {
    return form ? form.querySelector('.kit-builder') : null;
  }

  /* PDP ATC form invariant: the hidden name="id" input is the source of truth.
   * The widget's data-parent-variant-id is a fallback only. */
  function getParentVariantId(form, widget) {
    var idInput = form.querySelector('input[name="id"]');
    if (idInput && idInput.value) return idInput.value;
    widget = widget || findWidget(form);
    return widget ? widget.getAttribute('data-parent-variant-id') : null;
  }

  function getParentQty(form) {
    var qtyInput = form.querySelector('input[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!qty || qty < 1) qty = 1;
    return qty;
  }

  function getRows(widget) {
    return Array.prototype.slice.call(widget.querySelectorAll('.kit-builder__row'));
  }

  function getCheckedPick(row) {
    return row.querySelector('.kit-builder__pick:checked');
  }

  /* One pick per category, first checked wins. The name attribute already makes
   * picks exclusive in the browser; this is defense in depth for the payload. */
  function getPicks(widget) {
    var nodes = widget.querySelectorAll('.kit-builder__pick:checked');
    var picks = [];
    var seenCats = {};
    for (var i = 0; i < nodes.length; i++) {
      var cat = nodes[i].getAttribute('data-kit-cat-index');
      if (seenCats[cat]) continue;
      seenCats[cat] = true;
      picks.push(nodes[i]);
    }
    return picks;
  }

  function firstIncompleteRequiredRow(widget) {
    var rows = getRows(widget);
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-category-required') === 'true' && !getCheckedPick(rows[i])) {
        return rows[i];
      }
    }
    return null;
  }

  function categoryName(row) {
    return (row && row.getAttribute('data-category-name')) || 'item';
  }

  /* A category with exactly one purchasable option is not a choice: pre-select
   * it and collapse the row. Sold-out options are disabled in Liquid and never
   * auto-selected; a row with two or more live options stays an open question. */
  function autoSelectIncludedRows(widget) {
    var rows = getRows(widget);
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (getCheckedPick(row)) continue;
      var available = row.querySelectorAll('.kit-builder__pick:not([disabled])');
      if (available.length !== 1) continue;
      available[0].checked = true;
      row.classList.add('kit-builder__row--auto');
      row.removeAttribute('open');
    }
  }

  /* SET-PRICE MODEL: the kit sells as ONE line, the parent product, at its own
   * price ($75 Starter / $45 Mini). Picks are recorded as line item properties
   * for fulfillment and never charged separately. The footer therefore shows the
   * parent's live price and compare-at, not a component sum. */
  function computeTotals(widget) {
    var price = parseInt(widget.getAttribute('data-parent-price'), 10) || 0;
    var compare = parseInt(widget.getAttribute('data-parent-compare'), 10) || 0;
    return { subtotal: price, compareTotal: compare, anyCompare: compare > price };
  }

  function statusMessage(widget) {
    var incomplete = firstIncompleteRequiredRow(widget);
    if (incomplete) return 'Choose a ' + categoryName(incomplete).toLowerCase() + ' to continue.';
    return 'Click Add to Cart below';
  }

  function updateRowPills(widget) {
    var rows = getRows(widget);
    for (var i = 0; i < rows.length; i++) {
      var pill = rows[i].querySelector('[data-row-status]');
      if (!pill) continue;
      var pick = getCheckedPick(rows[i]);
      if (!pick) {
        pill.textContent = '';
      } else if (rows[i].classList.contains('kit-builder__row--auto')) {
        pill.textContent = 'Included';
      } else {
        pill.textContent = pick.getAttribute('data-title') || 'Selected';
      }
    }
  }

  function updateUi(widget) {
    var totals = computeTotals(widget);
    var totalEl = widget.querySelector('[data-kit-total]');
    var compareEl = widget.querySelector('[data-kit-compare]');
    var statusEl = widget.querySelector('[data-kit-status]');

    if (totalEl) totalEl.textContent = formatMoney(totals.subtotal);
    if (compareEl) {
      if (totals.anyCompare && totals.compareTotal > totals.subtotal) {
        compareEl.textContent = formatMoney(totals.compareTotal);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }
    if (statusEl) statusEl.textContent = statusMessage(widget);
    updateRowPills(widget);
  }

  function setStatus(widget, message) {
    var statusEl = widget.querySelector('[data-kit-status]');
    if (statusEl) statusEl.textContent = message;
  }

  /* Early Add to Cart: open, scroll to, flash and focus the missing row. */
  function flashRow(widget, row) {
    if (!row) return;
    row.setAttribute('open', '');
    try {
      row.scrollIntoView(REDUCED_MOTION ? true : { behavior: 'smooth', block: 'center' });
    } catch (e) {
      row.scrollIntoView();
    }
    row.classList.add('kit-builder__row--flash');
    setTimeout(function () {
      row.classList.remove('kit-builder__row--flash');
    }, 1200);
    var firstPick = row.querySelector('.kit-builder__pick:not([disabled])');
    if (firstPick) {
      try { firstPick.focus({ preventScroll: true }); } catch (e) {}
    }
    setStatus(widget, 'Choose a ' + categoryName(row).toLowerCase() + ' to continue.');
  }

  function bindWidget(widget) {
    if (widget.__kitBuilderBound) return;
    widget.__kitBuilderBound = true;

    autoSelectIncludedRows(widget);
    var firstIncomplete = firstIncompleteRequiredRow(widget);
    if (firstIncomplete) firstIncomplete.setAttribute('open', '');

    widget.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.classList || !t.classList.contains('kit-builder__pick')) return;
      updateUi(widget);
    });

    /* Let a click on an already-checked pick in an OPTIONAL category uncheck it
     * (named radios cannot natively deselect). Snapshot pre-click state on
     * pointerdown because by click time the browser has already re-checked it. */
    widget.addEventListener('pointerdown', function (e) {
      var t = e.target;
      if (!t || !t.classList || !t.classList.contains('kit-builder__pick')) return;
      t.__kbWasChecked = t.checked;
    });
    widget.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.classList || !t.classList.contains('kit-builder__pick')) return;
      if (t.__kbWasChecked && t.getAttribute('data-required') !== 'true') {
        t.checked = false;
        t.__kbWasChecked = false;
        updateUi(widget);
      }
    });

    /* Dynamic payment buttons (Buy It Now / wallets) would check out the parent
     * alone and skip the builder entirely, so they are hidden on kit PDPs only. */
    var form = widget.closest('form[action*="/cart/add"]');
    if (form) {
      var expressNodes = form.querySelectorAll('.shopify-payment-button, .additional-checkout-buttons, [data-shopify="payment-button"]');
      for (var i = 0; i < expressNodes.length; i++) expressNodes[i].style.display = 'none';

      /* Keyboard/Enter submits bypass the button click path; guard them too. */
      form.addEventListener('submit', function (e) {
        var incomplete = firstIncompleteRequiredRow(widget);
        if (!incomplete) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        flashRow(widget, incomplete);
      }, true);
    }

    updateUi(widget);
  }

  function initAll() {
    var widgets = document.querySelectorAll('.kit-builder');
    for (var i = 0; i < widgets.length; i++) bindWidget(widgets[i]);
  }

  function isExpressCheckoutButton(el) {
    if (!el || !el.closest) return false;
    return !!el.closest('.shopify-payment-button, .additional-checkout-buttons, [data-shopify="payment-button"]');
  }

  function setPending(btn, pending) {
    /* No innerHTML/textContent swaps: the theme's .push-btn-surface children carry
     * Alpine bindings that a text overwrite would destroy. State only. */
    if (pending) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('disabled', 'disabled');
    } else {
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('disabled');
    }
  }

  function handleSubmitClick(e) {
    var target = e.target;
    if (!target || !target.closest) return;
    if (isExpressCheckoutButton(target)) return;

    var submitBtn = target.closest('button[name="add"], [data-kit-atc], button[type="submit"]');
    if (!submitBtn) return;
    if (isExpressCheckoutButton(submitBtn)) return;

    var form = submitBtn.closest('form[action*="/cart/add"]');
    if (!form) return;

    var widget = findWidget(form);
    if (!widget) return;

    if (submitBtn.getAttribute('aria-busy') === 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    var incomplete = firstIncompleteRequiredRow(widget);
    if (incomplete) {
      e.preventDefault();
      e.stopImmediatePropagation();
      flashRow(widget, incomplete);
      return;
    }

    /* If neither the hidden id input nor the widget fallback has a variant id,
     * something is very wrong with the form; fall back to native submit. */
    var parentId = getParentVariantId(form, widget);
    if (!parentId) return;

    var picks = getPicks(widget);
    var qty = getParentQty(form);

    /* ONE line: the kit parent at its own price. Each picked category becomes a
     * visible line item property (e.g. Wax: I'm Sensitive...) for fulfillment. */
    var props = {};
    for (var i = 0; i < picks.length; i++) {
      var row = picks[i].closest('.kit-builder__row');
      props[categoryName(row)] = picks[i].getAttribute('data-title') || picks[i].value;
    }
    var items = [{
      id: parseInt(parentId, 10),
      quantity: qty,
      properties: props
    }];

    /* Merge checked Complete-your-routine upsells: this handler suppresses the
     * theme.liquid upsell listener on kit PDPs, so their adds happen here. */
    var upsells = form.querySelectorAll('.atc-upsell-checkbox:checked');
    for (var u = 0; u < upsells.length; u++) {
      var uid = parseInt(upsells[u].value, 10);
      if (uid) items.push({ id: uid, quantity: 1 });
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    setPending(submitBtn, true);

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(
          function (body) {
            var msg = (body && (body.description || body.message)) || 'That add did not go through, try again.';
            throw new Error(msg);
          },
          function () { throw new Error('That add did not go through, try again.'); }
        );
      }
      window.location.href = '/cart';
    }).catch(function (err) {
      setPending(submitBtn, false);
      setStatus(widget, (err && err.message) ? err.message : 'That add did not go through, try again.');
    });
  }

  /* Register the capture listener NOW, at parse time, so it precedes the
   * theme.liquid atc-upsells listener in document capture order. */
  document.addEventListener('click', handleSubmitClick, true);
  document.addEventListener('shopify:section:load', initAll);
  // Bind kit validation before early Alpine product submit listeners.
  document.addEventListener('shapes:theme:prepare', initAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
